import { create } from 'zustand'
import { DEFAULT_SOUND, type SoundName, soundOr } from './sounds.js'

/**
 * Per-device settings (FR-SET-02): sounds and which one, SFW mode, units, and the default view live in the browser.
 * Account settings (tile size, notifications) live on the server and come through the profile.
 */
export type Units = 'metric' | 'imperial'
export type PinPath = '/gate' | '/wall' | '/inbox'
export const PIN_PATHS: readonly PinPath[] = ['/gate', '/wall', '/inbox']
export type PinSide = 'left' | 'right'
export interface Pin {
  path: PinPath
  side: PinSide
}
/** Where each tab docks the first time: the Gate on the left, under its own tab; the others right. */
export const PIN_DEFAULT_SIDE: Record<PinPath, PinSide> = {
  '/gate': 'left',
  '/wall': 'right',
  '/inbox': 'right',
}
interface Prefs {
  sfw: boolean
  sounds: boolean
  /** The one the app plays, while sounds are on (decision 171). */
  sound: SoundName
  units: Units
  /**
   * The view the app opens to and keeps behind popups (FR-MAP-12): the last of /map and /grid you were
   * on (MapScreen saves it as you go).
   */
  defaultView: 'map' | 'grid'
  /**
   * Whether you have ever picked map or grid yourself, with the switch (decision 147). Until you have,
   * each start opens on whichever suits how many people are in view (map/startView.ts).
   */
  viewChosen: boolean
  /** WF-SAF-04: triple-tap the brand band to turn the app into something boring and sign out */
  quickExit: boolean
  /** Tab popups docked beside the map: one at tablet width, up to one a side on a wide screen. */
  pinned: Pin[]
  /** The side each tab was last moved to, so it docks there again next time. */
  pinSides: Partial<Record<PinPath, PinSide>>
  /** The Wall's order (bugs.md): by time, or by distance with Plus. */
  wallSort: 'time' | 'distance'
  /** Each other list's order (Sort): the Gate, the Inbox, Favorites' two halves, and the Chain's lists. */
  sorts: Partial<Record<SortList, string>>
  set: (p: Partial<Omit<Prefs, 'set'>>) => void
}
const KEY = 'fml.prefs'
function load(): Omit<Prefs, 'set'> {
  const base: Omit<Prefs, 'set'> = {
    sfw: false,
    sounds: true,
    sound: DEFAULT_SOUND,
    units: navigator.language === 'en-US' ? 'imperial' : 'metric',
    defaultView: 'map',
    viewChosen: false,
    quickExit: false,
    pinned: [],
    pinSides: {},
    wallSort: 'time',
    sorts: {},
  }
  try {
    const raw = localStorage.getItem(KEY)
    const stored = raw ? (JSON.parse(raw) as Partial<Prefs>) : {}
    const saved = { ...base, ...stored }
    // The grid was called the list until it had its own route.
    if ((saved.defaultView as string) === 'list') saved.defaultView = 'grid'
    // A sound that's since gone (or a bad value) plays the default.
    saved.sound = soundOr(saved.sound)
    // Saved before there was a flag: the app opened to the map, so a saved grid was picked by hand.
    if (stored.viewChosen === undefined) saved.viewChosen = saved.defaultView === 'grid'
    // One pin used to be saved alone (or null); now a list, never two on one side or of one tab.
    const pins = (Array.isArray(saved.pinned) ? saved.pinned : saved.pinned ? [saved.pinned] : []) as Pin[]
    saved.pinned = pins.filter(
      (p, i) =>
        PIN_PATHS.includes(p?.path) && pins.findIndex((q) => q.side === p.side || q.path === p.path) === i,
    )
    return saved
  } catch {
    return base
  }
}
export const usePrefs = create<Prefs>((set, get) => ({
  ...load(),
  set(p) {
    const next = { ...get(), ...p }
    const { set: _s, ...plain } = next
    try {
      localStorage.setItem(KEY, JSON.stringify(plain))
    } catch {}
    set(p)
  },
}))
/** The lists with a Sort of their own (the Wall keeps its `wallSort`). */
export type SortList = 'gate' | 'inbox' | 'cruisers' | 'spots' | 'grid' | 'vouched' | 'vetted'

/**
 * A list's order, as last picked on this device: one of `allowed`, else `fallback` (an order that has
 * since gone, or none picked yet).
 */
export function useSortPref<T extends string>(
  list: SortList,
  allowed: readonly T[],
  fallback: T,
): [T, (v: T) => void] {
  const saved = usePrefs((s) => s.sorts?.[list])
  const value = allowed.includes(saved as T) ? (saved as T) : fallback
  const set = (v: T) => usePrefs.getState().set({ sorts: { ...usePrefs.getState().sorts, [list]: v } })
  return [value, set]
}

/** Home: the view you were last on, map or grid, where closing a popup goes (FR-MAP-12). */
export const home = (): '/map' | '/grid' => (usePrefs.getState().defaultView === 'grid' ? '/grid' : '/map')
export const imperial = () => usePrefs.getState().units === 'imperial'
